import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Layout, ListItem, Select, Text} from '@phonectas/unifon-ui-kit';
import useBmoStore from 'common/stores/bmoStore';
import {shallow} from 'zustand/shallow';

interface SortValue {
    key: string;
    label: string;
    value: number[];
}

interface SortByCasesProps {
	onSort: (value: {key: string, label: string, value: number[]}) => void;
	sortByValue: SortValue;
}

const SortByCases: React.FC<SortByCasesProps> = ({onSort, sortByValue}) => {
	const [t] = useTranslation();
	const {getStatuses} = useBmoStore();
	const {statuses} = useBmoStore((state) => ({
		statuses: state.statuses as Record<string, number> | null,
	}), shallow);

	const items: SortValue[] = useMemo(() => {
		const defaultItems: SortValue[] = [
			{key: 'all', label: t('All'), value: []},
			{key: 'open', label: t('Open'), value: [1, 3, 4, 5]},
		];

		if (!statuses) {
			return defaultItems;
		}
		// Add status items dynamically based on the statuses object
		const statusItems: SortValue[] = Object.entries(statuses)
			.filter(([, value]) => typeof value === 'number')
			.map(([key, value]) => ({
				key,
				/* i18next-extract-disable-next-line */
				label: t(key),
				value: [value],
			}));

		return [...defaultItems, ...statusItems];
	}, [statuses, t]);

	const handleSort = useCallback(
		(val: unknown) => {
			if (typeof val !== 'string' && typeof val !== 'number') return;
			const selectedItem = items.find((item) => item.key === val);
			if (selectedItem) {
				onSort(selectedItem);
			}
		},
		[onSort, items]
	);

	useEffect(() => {
		if (!statuses) {
			getStatuses();
		}
	}, [getStatuses, statuses]);

	return (
		<Layout nowrap gap="sm" alignItems="center">
			<Text color="textSecondary" truncate={1} variant="body1">
				{t('Sort by')}:
			</Text>
			<Select
				alt
				onChange={(val) => handleSort(val)}
				value={sortByValue.key}
			>
				{items.map((item) => (
					<ListItem
						key={item.key}
						value={item.key}
						primaryText={item.label}
						selected={sortByValue.key === item.key}
					/>
				))}
			</Select>
		</Layout>
	);
}

export default React.memo(SortByCases);
